import { forwardRef, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import SuartePlusIcon from '@/assets/all-icons/suarte-plus-icon';
import SuartePlusShortIcon from '@/assets/all-icons/suarte-plus-short-icon';
import config from '@/config';
import ProfileDataContext from '@/context/ProfileDataContext';
import useCache from '@/hooks/useCache';
import useStateHandler from '@/hooks/useStateHandler';
import useTranslations from '@/i18n/useTranslations';
import ContinueButton from '@/shared-components/buttons/components/ContinueButton';
import ProfileAddButton from '@/shared-components/buttons/components/ProfileAddButton';
import ProfileContentLoader from '@/shared-components/loader/special-loaders/ProfileContentLoader.jsx';
import ActionRejectedPopup from '@/shared-components/popups/components/ActionRejectedPopup';
import SuartePlusPopup from '@/shared-components/popups/components/SuartePlusPopup';
import Text from '@/shared-components/text/components/Text';
import { canAccessGalleryPermission } from '@/utils/galleryPermissions';

import ProfileDrafts from '../board-drafts';
import PopUpDrafts from '../drafts/components/PopUpDrafts';
import MetaDescripProfilePages from '../MetaDescripProfilePages';
import PopUpCreateOptionsBoard from '../post/components/PopUpCreateOptionsBoard';

import ArticlesVirtualList from './components/ArticlesVirtualList';
import PostVirtualList    from './components/PostVirtualList';

import './index.css';

const VIP_AUDIENCE_VALUES = new Set([
  'vip_only',
  'vip',
  'studio_only',
  'studio',
  'subscriber_only',
  'subscribers_only',
]);

function normalizeAudienceValue(value) {
  if (value === null || value === undefined) {
    return null;
  }

  const normalized = String(value)
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');

  if (!normalized) {
    return null;
  }

  return normalized;
}

function isVipOnlyContent(item) {
  const audience = normalizeAudienceValue(
    item?.audience ?? item?.post_container?.audience
  );

  return audience ? VIP_AUDIENCE_VALUES.has(audience) : false;
}

export default function ProfileBoard() {
  const t = useTranslations('common.profile.pages.board');
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const listRef = useRef(null);
  const { profileData, internal } = useContext(ProfileDataContext);
  const { state } = useStateHandler();
  const profileId = profileData?._id;

  const [openOptions, setOpenOptions] = useState(false);
  const [openDraftPopup, setOpenDraftPopup] = useState(false);
  const [queuedPosts, setQueuedPosts] = useState([]);
  const [queuedArticles, setQueuedArticles] = useState([]);
  const [showSuartePlusPopup, setShowSuartePlusPopup] = useState(false);
  const [showActionRejectedPopup, setShowActionRejectedPopup] = useState(false);
  const [postsStatus, setPostsStatus] = useState({
    loading: true,
    hasItems: true,
    publishedCount: 0,
    queuedCount: 0,
  });
  const [hasOpenedArticlesTab, setHasOpenedArticlesTab] = useState(
    searchParams.get('view') === 'articles'
  );

  const handlePostsStatusChange = useCallback(({ loading, hasItems }) => {
    setPostsStatus(prev => {
      if (prev.loading === loading && prev.hasItems === hasItems) {
        return prev;
      }
      return { loading, hasItems };
    });
  }, []);

  const userType = String(profileData?.user_type ?? profileData?.__t ?? "").toLowerCase();
  const isGallery = userType === "gallery";
  const isArtist = userType === "artist";
  const hasSuartePlus = Boolean(profileData?.suartePlus?.isActive);
  const session = state.user_session;
  const sessionType = String(session?.user_type ?? session?.__t ?? "").toLowerCase();
  const sessionId = session?._id ?? session?.user_id ?? null;
  const isGalleryOwner = sessionType === "gallery" && sessionId === profileId;
  const isArtistOwner = sessionType === "artist" && sessionId === profileId;
  const allowPosts = isGallery
    ? (isGalleryOwner || canAccessGalleryPermission(session, "board.post", profileId))
    : isArtist
      ? isArtistOwner
      : hasSuartePlus;
  const allowArticles = isGallery
    ? (isGalleryOwner || canAccessGalleryPermission(session, "board.article", profileId))
    : isArtist
      ? isArtistOwner
      : false;
  const canCreatePosts = internal && (isGallery || isArtist || hasSuartePlus);
  const canOpenCreateOptions = allowPosts || allowArticles;
  const showSuartePlusUpsell = internal && !isGallery && !isArtist && !hasSuartePlus;
  const availableTabs = useMemo(
    () => ((isGallery || isArtist) ? ['posts', 'articles'] : ['posts']),
    [isArtist, isGallery]
  );
  const defaultTab = availableTabs[0];
  const rawView = searchParams.get('view');
  const view = availableTabs.includes(rawView) ? rawView : defaultTab;

  useEffect(() => {
    if (view === 'articles' && !hasOpenedArticlesTab) {
      setHasOpenedArticlesTab(true);
    }
  }, [hasOpenedArticlesTab, view]);

  const {
    loading: articlesLoading,
    fetchData: articlesFetchData,
    loadMoreData: loadMoreArticles,
  } = useCache(
    `${profileId}_articles`,
    `${config.apis.api.url}/posts/${userType}/articles/${profileId}`,
    {
      injectToken: true,
      disabled: !hasOpenedArticlesTab,
      invalidateWhen: internal
        ? ['SUBMIT_POST', 'DELETE_POST', `${profileId}_REFRESH`]
        : [`${profileId}_REFRESH`],
    }
  );
  const safeArticles = articlesFetchData ?? { data: [], totalDocs: 0 };
  const partitionedArticles = useMemo(
    () => partitionArticlesForBoard(safeArticles.data),
    [safeArticles.data]
  );
  const publishedArticlesCount = partitionedArticles.published.length;
  const articlesHasContent =
    partitionedArticles.published.length > 0 ||
    partitionedArticles.queued.length > 0;
  const boardSeoCount = view === 'articles'
    ? publishedArticlesCount
    : postsStatus.publishedCount;

  // ensure the query param always matches one of the available tabs
  useEffect(() => {
    if (!rawView || !availableTabs.includes(rawView)) {
      setSearchParams({ view: defaultTab }, { replace: true });
    }
  }, [availableTabs, defaultTab, rawView, setSearchParams]);

  useEffect(() => {
    if (!isGallery && !isArtist) return;
    if (!hasOpenedArticlesTab) return;

    if (
      view === 'posts' &&
      postsStatus.loading === false &&
      postsStatus.hasItems === false &&
      articlesHasContent
    ) {
      setSearchParams(
        { view: 'articles' },
        { replace: true, preventScrollReset: true, state: location.state }
      );
    }
  }, [
    articlesHasContent,
    isArtist,
    isGallery,
    hasOpenedArticlesTab,
    location.state,
    postsStatus.hasItems,
    postsStatus.loading,
    setSearchParams,
    view,
  ]);

  return (
    <>
      <MetaDescripProfilePages
        fetchData={profileData}
        subPage="board"
        internal={internal}
        itemCount={boardSeoCount}
        boardView={view}
      />
      {canCreatePosts && (
        <>
          <ProfileAddButton
            label={t('buttons.post_to_board')}
            onClick={() => {
              if (canOpenCreateOptions) {
                setOpenOptions(true);
                return;
              }
              setShowActionRejectedPopup(true);
            }}
          />

          <PopUpCreateOptionsBoard
            open={openOptions}
            close={() => setOpenOptions(false)}
            openDraftPopup={setOpenDraftPopup}
            queuedPosts={queuedPosts}
            queuedArticles={queuedArticles}
            allowPosts={allowPosts}
            allowArticles={allowArticles}
          />

          <PopUpDrafts
            open={openDraftPopup}
            close={() => setOpenDraftPopup(false)}
            DraftsList={props => <ProfileDrafts type={view} {...props} />}
          />
          <ActionRejectedPopup
            open={showActionRejectedPopup}
            close={() => setShowActionRejectedPopup(false)}
            description={t('messages.permission_denied')}
          />
        </>
      )}
      {/* ————— Tabs ————— */}
      <div className="user-profile__board-navigation element-non-selectable">
        {availableTabs.map(tab => (
          <div
            key={tab}
            className={`user-profile__board-navigation-link ${
              view === tab ? 'active' : ''
            }`}
            onClick={() => {
              if (tab === 'articles') {
                setHasOpenedArticlesTab(true);
              }
              setSearchParams({ view: tab });
            }}
          >
            <Text
              className="user-profile__board-navigation-link-text"
              extraSmall
            >
              {tab === 'posts' ? t('tabs.posts') : t('tabs.articles')}
            </Text>
          </div>
        ))}
      </div>
      {showSuartePlusUpsell && (
        <div className="user-profile__board-upsell-wrapper">
          <Text className="user-profile__board-upsell font-size-small" paragraph>
            {t('upsell.prefix')}{" "}
            <SuartePlusIcon className="user-profile__board-upsell-icon" aria-hidden="true" /> {t('upsell.middle')} {t('upsell.suffix')}
          </Text>
          <ContinueButton
            className="user-profile__board-upsell-button"
            onClick={() => setShowSuartePlusPopup(true)}
          >
            {t('upsell.cta')}
          </ContinueButton>
        </div>
      )}
      {/* ————— Content Area ————— */}
      {showSuartePlusUpsell ? (
        <></>
      ) : view === 'posts' ? (
        <PostsTab
          key={`posts-${location.key}`}
          profileId={profileData._id}
          internal={internal}
          onQueuedChange={setQueuedPosts}
          onStatusChange={handlePostsStatusChange}
          ref={listRef}                   // ← pass our ref into PostsTab
        />
      ) : (
        <>
          {(isGallery || isArtist) && (
            <ArticlesTab
              key={`articles-${location.key}`}
              profileId={profileData._id}
              internal={internal}
              ref={listRef}                   // ← …and into ArticlesTab
              onQueuedChange={setQueuedArticles}
              articlesState={{
                loading: articlesLoading,
                items: safeArticles,
                publishedItems: partitionedArticles.published,
                queuedItems: partitionedArticles.queued,
                onLoadMore: loadMoreArticles,
              }}
            />
          )}
        </>
      )}
      <SuartePlusPopup
        open={showSuartePlusPopup}
        onClose={() => setShowSuartePlusPopup(false)}
        highlightedBenefit="posts"
        actionTitle={
          <span className="suarte-plus-popup__title-inline">
            {t('suarte_plus.action_prefix')} <SuartePlusShortIcon className="suarte-plus-popup__title-icon" />
          </span>
        }
      />
    </>
  );
}

/** ─── POSTS TAB ──────────────────────────────────────────────────────────
 * Each tab now owns its own useCache, so flipping tabs unmounts one and
 * mounts the other—triggering a fresh fetch immediately.
 */

/** ─── POSTS TAB (match ArticlesTab behavior) ───────────────────────── */
const PostsTab = forwardRef(function PostsTab(
  { profileId, internal, onQueuedChange, onStatusChange },
) {
  const t = useTranslations('common.profile.pages.board');
  const { profileData } = useContext(ProfileDataContext);
  const userType = String(profileData?.user_type ?? profileData?.__t ?? '').toLowerCase();
  const profileName = profileData?.user_username || profileData?.user_name || t('defaults.profile_name');
  const endpoint = `${config.apis.api.url}/posts/${userType}/posts/${profileId}`;
  const cacheKey = `${profileId}_posts`;

  const { loading, fetchData, loadMoreData } = useCache(
    cacheKey,
    endpoint,
    {
      injectToken: true,
      invalidateWhen: internal
        ? ['SUBMIT_POST', 'DELETE_POST', `${profileId}_REFRESH`]
        : [`${profileId}_REFRESH`],
    }
  );

  const safe = fetchData ?? { data: [], totalDocs: 0 };

  const partitionedPosts = useMemo(() => {
    const now = Date.now();
    const grouped = { published: [], queued: [] };

    safe.data.forEach(item => {
      if (isVipOnlyContent(item)) {
        return;
      }

      const scheduledISO = item.publishedAt ?? item.post_container?.publishedAt;
      const scheduledMs = scheduledISO ? Date.parse(scheduledISO) : NaN;
      const hasFutureSchedule = Number.isFinite(scheduledMs) && scheduledMs > now;

      if (hasFutureSchedule) {
        grouped.queued.push(item);
      } else {
        grouped.published.push(item);
      }
    });

    // Ordenar "queued" del más próximo al más lejano (igual que en ArticlesTab)
    grouped.queued.sort((a, b) => {
      const aDate = Date.parse(a?.publishedAt ?? a?.post_container?.publishedAt ?? '');
      const bDate = Date.parse(b?.publishedAt ?? b?.post_container?.publishedAt ?? '');
      return aDate - bDate;
    });

    return grouped;
  }, [safe.data]);

  const publishedPosts = partitionedPosts.published;
  const queuedPosts = partitionedPosts.queued;
  const hasQueued = queuedPosts.length > 0;

  const displayItems = publishedPosts;
  const shouldLoadMore = safe.data.length < safe.totalDocs;
  const autoLoadCheckpointRef = useRef(0);

  useEffect(() => {
    onQueuedChange?.(queuedPosts);
  }, [queuedPosts, onQueuedChange]);

  useEffect(() => {
    onStatusChange?.({
      loading,
      hasItems: publishedPosts.length > 0 || queuedPosts.length > 0,
      publishedCount: publishedPosts.length,
      queuedCount: queuedPosts.length,
    });
  }, [loading, onStatusChange, publishedPosts.length, queuedPosts.length]);

  useEffect(() => () => {
    onQueuedChange?.([]);
  }, [onQueuedChange]);

  useEffect(() => {
    if (displayItems.length === 0 && shouldLoadMore && safe.data.length > autoLoadCheckpointRef.current) {
      autoLoadCheckpointRef.current = safe.data.length;
      loadMoreData();
    }
    if (!shouldLoadMore) {
      autoLoadCheckpointRef.current = 0;
    }
  }, [displayItems.length, shouldLoadMore, safe.data.length, loadMoreData]);

  if (loading) {
    return <ProfileContentLoader className="profile-board__posts-spinner" />;
  }

  if (publishedPosts.length === 0 && queuedPosts.length === 0) {
    return (
      <Text className="user-profile__board-empty-view font-size-medium" paragraph>
        {internal
          ? t('empty.posts_internal')
          : t('empty.posts_external', { name: profileName })}
      </Text>
    );
  }

  if (publishedPosts.length === 0 && hasQueued) {
    return (
      <Text className="posts-queued__empty-hint font-size-small" paragraph>
        {internal
          ? t('empty.posts_queued_internal')
          : t('empty.posts_queued_external', { name: profileName })}
      </Text>
    );
  }

  return (
    <PostVirtualList
      items={{ ...safe, data: displayItems }}
      onLoadMore={loadMoreData}
    />
  );
});

/** ─── ARTICLES TAB ───────────────────────────────────────────────────── */
const ArticlesTab = forwardRef(function ArticlesTab(
  { internal, onQueuedChange, articlesState },
) {
  const t = useTranslations('common.profile.pages.board');
  const {
    loading = false,
    items = { data: [], totalDocs: 0 },
    publishedItems = [],
    queuedItems = [],
    onLoadMore = () => {},
  } = articlesState ?? {};

  useEffect(() => {
    onQueuedChange?.(queuedItems);
  }, [queuedItems, onQueuedChange]);

  useEffect(() => () => {
    onQueuedChange?.([]);
  }, [onQueuedChange]);

  if (loading) {
    return <ProfileContentLoader className="profile-board__posts-spinner" />;
  }

  if (publishedItems.length === 0 && queuedItems.length === 0) {
    return (
      <Text className="user-profile__board-empty-view font-size-medium" paragraph>
        {internal
          ? t('empty.articles_internal')
          : t('empty.articles_external')}
      </Text>
    );
  }

  return (
    <ArticlesVirtualList
      items={items}
      publishedItems={publishedItems}
      queuedItems={queuedItems}
      onLoadMore={onLoadMore}
      cardVariant="board"
    />
  );
});

function partitionArticlesForBoard(items = []) {
  const now = Date.now();
  const grouped = { published: [], queued: [] };

  items.forEach(item => {
    if (isVipOnlyContent(item)) {
      return;
    }

    const title = item?.post_container?.post_title?.trim();
    if (!title) return;

    const scheduledISO = item?.publishedAt ?? item?.post_container?.publishedAt;
    const scheduledMs = scheduledISO ? Date.parse(scheduledISO) : NaN;
    const hasFutureSchedule = Number.isFinite(scheduledMs) && scheduledMs > now;

    if (hasFutureSchedule) {
      grouped.queued.push(item);
    } else {
      grouped.published.push(item);
    }
  });

  grouped.queued.sort((a, b) => {
    const aDate = Date.parse(a?.publishedAt ?? a?.post_container?.publishedAt ?? '');
    const bDate = Date.parse(b?.publishedAt ?? b?.post_container?.publishedAt ?? '');
    return aDate - bDate;
  });

  return grouped;
}
